import type { ComponentProps, ReactNode } from 'react';
import React from 'react';

import { ListboxOption } from '@headlessui/react';
import clsx from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import type {
    ChildrenRenderProps,
    OptionType,
    SelectComponentProps
} from '@/components/base/Select/SelectComponent/types';

import { optionsSizeStyle } from '@/components/base/Select/SelectComponent/selectConfig';
import { DefaultSelectOption } from '@/components/base/Select/SelectComponent/SubComponents/SelectOption/DefaultOption';

type OptionContainerProps<TOption, TSelected> = Pick<
    SelectOptionProps<TOption, TSelected>,
    'size'
> &
    Omit<ComponentProps<typeof ListboxOption>, 'value' | 'disabled' | 'children'> & {
        option: TOption;
        children?: (args: {
            focus: boolean;
            selected: boolean;
            disabled: boolean;
            option: TOption;
        }) => React.ReactElement;
    };

function OptionContainer<TOption extends OptionType, TSelected extends TOption | TOption[]>({
    children,
    option,
    size = 'default',
    ...restProps
}: OptionContainerProps<TOption, TSelected>) {
    return (
        <ListboxOption
            data-testid="select-option-container"
            id={option.label}
            value={option}
            disabled={option?.disabled ?? null}
            className={({ focus, selected, disabled }) => {
                return clsx(
                    'relative cursor-pointer select-none rounded-md outline-none duration-200',
                    optionsSizeStyle[size],
                    {
                        'text-icoBlue': selected,
                        'bg-icoBlue-100 text-icoBlue dark:bg-icoBlue/30': focus,
                        'pointer-events-none text-icoGray-400': disabled
                    }
                );
            }}
            {...restProps}>
            {({ focus, selected, disabled }) => {
                const content =
                    typeof children === 'function'
                        ? children({ focus, selected, disabled, option })
                        : children;
                return <>{content}</>;
            }}
        </ListboxOption>
    );
}

type OptionProps<TOption, TSelected> = Omit<SelectOptionProps<TOption, TSelected>, 'children'> & {
    children: ChildrenRenderProps<TOption>;
};

export function Option<TOption extends OptionType, TSelected extends TOption | TOption[]>({
    options,
    isMultiple,
    selected,
    size,
    children,
    ...restProps
}: OptionProps<TOption, TSelected>) {
    const { t } = useTranslation('commons');
    if (Array.isArray(options) && !options.length) {
        return <div className="py-1 text-center">{t('select_no_options')}</div>;
    }

    return (
        <>
            {options?.map((option, id) => {
                const selectedOptions = selected;

                const isSelected = (selected: boolean) => {
                    if (isMultiple && Array.isArray(selectedOptions)) {
                        return Boolean(selectedOptions?.find((el) => el?.value === option?.value));
                    }
                    return selected;
                };

                return (
                    <OptionContainer key={id} option={option} size={size}>
                        {({ selected, disabled, focus, option }) => {
                            if (!children) {
                                return (
                                    <DefaultSelectOption
                                        option={option}
                                        selected={isSelected(selected)}
                                        disabled={disabled}
                                        isMultiple={isMultiple}
                                        {...restProps}
                                    />
                                );
                            }
                            const result = children({
                                selected: isSelected(selected),
                                option,
                                disabled,
                                focus
                            });
                            return <>{result}</>;
                        }}
                    </OptionContainer>
                );
            })}
        </>
    );
}

export type SelectOptionProps<TOption, TSelected> = Pick<
    SelectComponentProps<TOption, TSelected>,
    'options' | 'selected'
> & {
    isTruncate: SelectComponentProps<TOption, TSelected>['panelOptions']['isTruncate'];
    forcedDisplay: SelectComponentProps<TOption, TSelected>['layoutOptions']['forcedDisplay'];
    isMultiple: SelectComponentProps<TOption, TSelected>['layoutOptions']['isMultiple'];
    size: SelectComponentProps<TOption, TSelected>['panelOptions']['size'];
    children: ChildrenRenderProps<TOption> | ReactNode;
};

export function SelectOption<TOption extends OptionType, TSelected extends TOption | TOption[]>({
    options,
    isMultiple,
    selected,
    children,
    size,
    isTruncate,
    forcedDisplay
}: SelectOptionProps<TOption, TSelected>) {
    if (options) {
        return (
            <Option
                size={size}
                options={options}
                isMultiple={isMultiple}
                selected={selected}
                isTruncate={isTruncate}
                forcedDisplay={forcedDisplay}>
                {children as ChildrenRenderProps<TOption>}
            </Option>
        );
    }
    return children as ReactNode;
}
